import React, { useMemo, useState, useEffect } from 'react';
import { Plus, MapPin, Sparkles, Cigarette, BarChart3, Star, Users, Globe, Bookmark, Sun, Moon, ShieldCheck, RefreshCcw } from 'lucide-react';
import { User, CigarLog, AppView, HumidorItem } from '../types';
import { LazyImage } from './LazyImage';
import { isAndroid } from '../utils/platformUtils';

interface DashboardProps {
    user: User;
    logs: CigarLog[];
    humidorItems?: HumidorItem[];
    onNavigate: (view: AppView) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, logs, humidorItems = [], onNavigate }) => {
    const showUpdateInline = isAndroid();
    const communityEnabled = !isAndroid();

    // Ritual Engine State
    const [ritual, setRitual] = useState<{
        greeting: string;
        context: string;
        suggestion?: HumidorItem;
        icon: React.ReactNode;
        themeColor: string;
        date: string;
    } | null>(null);

    useEffect(() => {
        const updateRitual = () => {
            const now = new Date();
            const hour = now.getHours();

            // Date Formatting (e.g., "Saturday, Dec 27")
            const dateOptions: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'short', day: 'numeric' };
            const dateString = now.toLocaleDateString('en-US', dateOptions);

            let greeting = '';
            let context = '';
            let icon: React.ReactNode = <Sun className="text-gold-500" />;
            let themeColor = 'from-orange-500/20 to-amber-500/20';
            let suggestedCigar: HumidorItem | undefined;

            // 1. Determine Time Context
            if (hour >= 5 && hour < 12) {
                greeting = 'Morning Ritual';
                context = 'Perfect time for a light Connecticut with coffee.';
                icon = <Sun className="text-gold-400" size={20} />;
                themeColor = 'from-amber-200/10 to-orange-100/5';
                suggestedCigar = humidorItems.find(i => (i.wrapper || '').toLowerCase().includes('connecticut') || (i.wrapper || '').toLowerCase().includes('claro'));
            } else if (hour >= 12 && hour < 17) {
                greeting = 'Afternoon Session';
                context = 'A moment of clarity. Perhaps a medium-bodied Habano?';
                icon = <Sun className="text-amber-400" size={20} />;
                themeColor = 'from-amber-900/40 to-orange-900/20'; // Warmer, no blue
                suggestedCigar = humidorItems.find(i => (i.wrapper || '').toLowerCase().includes('habano') || (i.wrapper || '').toLowerCase().includes('sun'));
            } else {
                greeting = 'Evening Unwind';
                context = 'Reward yourself. A rich Maduro and a stiff pour.';
                icon = <Moon className="text-gold-500" size={20} />;
                themeColor = 'from-brand-900/60 to-black/40'; // Dark/Premium
                suggestedCigar = humidorItems.find(i => (i.wrapper || '').toLowerCase().includes('maduro') || (i.wrapper || '').toLowerCase().includes('oscuro'));
            }

            // Fallback suggestion if no match found
            if (!suggestedCigar && humidorItems.length > 0) {
                suggestedCigar = humidorItems[Math.floor(Math.random() * humidorItems.length)];
            }

            setRitual({ greeting, context, suggestion: suggestedCigar, icon, themeColor, date: dateString });
        };

        updateRitual(); // Run immediately
        const interval = setInterval(updateRitual, 60000); // Update every minute
        return () => clearInterval(interval);
    }, [humidorItems]);


    // Stats Logic
    const stats = useMemo(() => {
        const total = logs.length;

        // Monthly
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const monthlyCount = logs.filter(l => {
            const d = new Date(l.date);
            return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
        }).length;

        // Favorite Brand
        const brandCounts: Record<string, number> = {};
        logs.forEach(l => {
            const b = l.brand.trim();
            if (b) brandCounts[b] = (brandCounts[b] || 0) + 1;
        });
        const favoriteBrand = Object.entries(brandCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

        // Average Rating
        const avgRating = logs.length > 0
            ? (logs.reduce((sum, l) => sum + (l.rating || 0), 0) / logs.length).toFixed(1)
            : '—';

        return { total, monthlyCount, favoriteBrand, avgRating };
    }, [logs]);

    const lastSmoke = logs.length > 0 ? logs[0] : null;

    const shortcuts = [
        { view: AppView.SOMMELIER, icon: Sparkles, title: 'AI Sommelier', desc: 'Expert pairing advice', delay: 'stagger-1' },
        { view: AppView.YOUR_NEXT_STICK, icon: Sparkles, title: 'Your Next Stick', desc: 'AI personalized picks', delay: 'stagger-2' },
        { view: AppView.LOCATOR, icon: MapPin, title: 'Find Lounge', desc: 'Nearby cigar spots', delay: 'stagger-3' },
        { view: AppView.ANALYTICS, icon: BarChart3, title: 'Analytics', desc: 'Your smoking trends', delay: 'stagger-4' },
        ...(communityEnabled ? [{ view: AppView.COMMUNITY, icon: Users, title: 'Community', desc: 'See what is trending', delay: 'stagger-5' }] : []),
        { view: AppView.JOURNEY, icon: Globe, title: 'Journey Map', desc: 'Your cigar adventures', delay: 'stagger-6' },
        { view: AppView.WISHLIST, icon: Bookmark, title: 'Wishlist', desc: 'Cigars to try next', delay: 'stagger-6' },
    ];

    if (!ritual) return null; // Or skeleton

    return (
        <div className="min-h-full w-full overflow-y-auto bg-brand-950 px-4 py-6 md:p-12 pb-36 md:pb-12">

            {/* Ambient Background Glow */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold-500/5 rounded-full blur-[100px] animate-float"></div>
                <div className="absolute -bottom-48 -left-32 w-[500px] h-[500px] bg-gold-600/3 rounded-full blur-[120px]"></div>
            </div>

            <div className="relative z-10 max-w-4xl mx-auto">

                {/* RITUAL WIDGET (Replaces Header) */}
                <header className="mb-8 animate-fade-in relative group">
                    {/* "Golden Thread" Top Connector */}
                    <div className="absolute -top-12 left-6 h-12 w-px golden-thread opacity-50 hidden md:block"></div>

                    <div className={`glass-card rounded-2xl p-6 md:p-8 bg-gradient-to-br ${ritual.themeColor} border border-white/5 relative overflow-hidden transition-all duration-500 hover:shadow-glow-gold`}>
                        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-6">
                            <div>
                                <div className="flex items-center gap-2 mb-2 text-gold-500 text-xs font-bold uppercase tracking-widest">
                                    {ritual.icon}
                                    <span>{ritual.greeting}</span>
                                    <span className="text-brand-600">•</span>
                                    <span className="text-brand-400">{ritual.date}</span>
                                </div>
                                <h1 className="text-3xl md:text-5xl font-serif text-brand-50 leading-tight mb-2 flex items-center flex-wrap gap-x-4">
                                    Hello, <span className="text-gradient-gold font-bold">{user.displayName?.split(' ')[0] || 'Aficionado'}</span>.
                                    {logs.length >= 10 && (
                                        <div className="inline-flex items-center gap-1.5 bg-gold-500 border border-gold-500/20 px-3 py-1.5 rounded-full text-[10px] md:text-xs font-bold text-black uppercase tracking-widest shadow-lg backdrop-blur-sm animate-fade-in" title="Certified Smoker: 10+ entries">
                                            <ShieldCheck size={14} className="fill-black/20" />
                                            <span>Certified Smoker</span>
                                        </div>
                                    )}
                                </h1>
                                <p className="text-brand-300 md:text-lg max-w-md font-light leading-relaxed">
                                    {ritual.context}
                                </p>
                                {showUpdateInline && (
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="mt-3 inline-flex items-center gap-1.5 bg-gold-500 hover:bg-gold-400 px-3 py-1.5 rounded-full transition-all shadow-xl active:scale-95 z-20 overflow-hidden"
                                        title="Refresh App"
                                    >
                                        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <RefreshCcw size={12} className="text-black transition-transform duration-700 relative z-10" />
                                        <span className="text-[10px] font-bold text-black uppercase tracking-wider relative z-10">Update</span>
                                    </button>
                                )}
                            </div>

                            {/* Manual Refresh Button (Bottom Right) */}
                            {!showUpdateInline && (
                                <button
                                    onClick={() => window.location.reload()}
                                    className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-gold-500 hover:bg-gold-400 px-3 py-1.5 rounded-full transition-all group shadow-xl active:scale-95 z-20 overflow-hidden"
                                    title="Refresh App"
                                >
                                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                    <RefreshCcw size={12} className="text-black group-active:rotate-180 transition-transform duration-700 relative z-10" />
                                    <span className="text-[10px] font-bold text-black uppercase tracking-wider relative z-10">Update</span>
                                </button>
                            )}

                            {/* Suggestion Card */}
                            {ritual.suggestion && (
                                <div className="bg-black/30 backdrop-blur-md rounded-xl p-4 border border-white/10 flex items-center gap-4 min-w-[280px] hover:bg-black/40 transition-colors cursor-pointer" onClick={() => onNavigate(AppView.HUMIDOR)}>
                                    <div className="w-12 h-12 rounded-lg bg-brand-800 flex items-center justify-center text-brand-400">
                                        <Cigarette size={20} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gold-500 font-bold uppercase">From your Humidor</p>
                                        <p className="text-brand-50 font-serif text-lg leading-tight truncate max-w-[180px]">{ritual.suggestion.brand}</p>
                                        <p className="text-brand-400 text-sm truncate max-w-[180px]">{ritual.suggestion.name}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* STATS ROW - Horizontal scroll on mobile */}
                <div className="flex gap-3 mb-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide animate-fade-in-up">
                    <div className="flex-shrink-0 glass-card rounded-2xl p-4 min-w-[140px] hover:border-gold-500/30 transition-all">
                        <p className="text-brand-400 text-xs uppercase tracking-wider mb-1">This Month</p>
                        <p className="text-3xl font-serif font-bold text-brand-50">{stats.monthlyCount}</p>
                        <p className="text-brand-500 text-sm">cigars</p>
                    </div>
                    <div className="flex-shrink-0 glass-card rounded-2xl p-4 min-w-[140px] hover:border-gold-500/30 transition-all">
                        <p className="text-brand-400 text-xs uppercase tracking-wider mb-1">Total Logged</p>
                        <p className="text-3xl font-serif font-bold text-brand-50">{stats.total}</p>
                        <p className="text-brand-500 text-sm">all time</p>
                    </div>
                    <div className="flex-shrink-0 glass-card rounded-2xl p-4 min-w-[140px] hover:border-gold-500/30 transition-all">
                        <p className="text-brand-400 text-xs uppercase tracking-wider mb-1">Avg Rating</p>
                        <p className="text-3xl font-serif font-bold text-gold-500">{stats.avgRating}</p>
                        <p className="text-brand-500 text-sm">out of 5</p>
                    </div>
                </div>

                {/* PRIMARY ACTION: LOG SMOKE */}
                <div
                    className="gold-gradient rounded-3xl p-6 mb-6 relative overflow-hidden cursor-pointer group animate-fade-in-up animate-pulse-glow"
                    onClick={() => onNavigate(AppView.LOG_SMOKE)}
                >
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -mr-12 -mt-12 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/20 rounded-full blur-2xl -ml-8 -mb-8 pointer-events-none"></div>

                    <div className="relative z-10 flex items-center justify-between">
                        <div>
                            <p className="text-gold-100/90 text-xs font-semibold uppercase tracking-widest mb-1 flex items-center gap-1.5 shadow-sm">
                                <Plus size={12} strokeWidth={3} />
                                Quick Action
                            </p>
                            <h2 className="text-2xl font-serif font-bold text-brand-50 text-shadow-soft">Log a Smoke</h2>
                            <p className="text-gold-100/80 text-sm mt-1 max-w-[200px] font-medium">Capture your tasting notes & rating</p>
                        </div>
                        <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm group-hover:scale-110 group-active:scale-95 transition-transform duration-300 shadow-lg border border-white/20">
                            <Cigarette size={28} className="text-white" strokeWidth={1.5} />
                        </div>
                    </div>
                </div>

                {/* LAST SMOKED CARD */}
                {lastSmoke ? (
                    <div className="premium-card rounded-3xl overflow-hidden mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                        {/* Background Image */}
                        {lastSmoke.image && (
                            <div className="absolute inset-0">
                                <LazyImage
                                    src={lastSmoke.image}
                                    alt={lastSmoke.name}
                                    className="w-full h-full object-cover opacity-15"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-brand-950 via-brand-950/90 to-brand-950/70"></div>
                            </div>
                        )}

                        <div className="relative z-10 p-5">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <p className="text-gold-500 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                        <Star size={12} className="fill-current" />
                                        Last Smoked
                                    </p>
                                    <h3 className="text-xl font-serif font-bold text-gold-600">{lastSmoke.brand}</h3>
                                    <p className="text-gold-700">{lastSmoke.name}</p>
                                </div>

                                {/* Rating Badge */}
                                <div className="bg-brand-900/80 backdrop-blur-sm rounded-xl px-3 py-2 border border-brand-800 shadow-inner">
                                    <p className="text-2xl font-serif font-bold text-gold-500">{lastSmoke.rating}</p>
                                    <p className="text-brand-500 text-xs text-center">/5</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            size={14}
                                            className={i < lastSmoke.rating ? 'text-gold-500 fill-gold-500 filter drop-shadow-sm' : 'text-brand-700'}
                                        />
                                    ))}
                                </div>

                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="glass-card rounded-3xl p-8 mb-6 text-center animate-fade-in-up border-dashed" style={{ animationDelay: '0.1s' }}>
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-900 flex items-center justify-center">
                            <Cigarette className="text-brand-600" size={28} />
                        </div>
                        <p className="text-brand-400 font-medium mb-1">No cigars logged yet</p>
                        <p className="text-brand-600 text-sm">Your journey begins with the first puff.</p>
                    </div>
                )}

                {/* FEATURE SHORTCUTS - 2-column grid on mobile */}
                <div className="mb-4">
                    <h3 className="text-brand-400 text-xs font-semibold uppercase tracking-widest mb-4 flex items-center gap-2">
                        <span className="w-8 h-px bg-brand-800"></span>
                        Services
                    </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {shortcuts.map((item) => (
                        <div
                            key={item.view}
                            className={`premium-card rounded-2xl p-4 cursor-pointer group animate-fade-in-up opacity-0 ${item.delay}`}
                            onClick={() => onNavigate(item.view)}
                        >
                            <div className="w-11 h-11 rounded-xl bg-brand-950 flex items-center justify-center mb-3 group-hover:bg-gold-600/20 transition-colors border border-brand-800/50 group-hover:border-gold-500/30">
                                <item.icon className="text-gold-500 group-hover:icon-glow transition-all" size={22} />
                            </div>
                            <h4 className="text-brand-50 font-semibold text-sm mb-0.5">{item.title}</h4>
                            <p className="text-brand-500 text-xs leading-tight">{item.desc}</p>
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
};
